
import React, { useState, useEffect } from 'react';
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Menu } from "lucide-react"

import Logo from './Logo';

const NavLink = React.forwardRef<
  React.ElementRef<typeof RouterNavLink>,
  React.ComponentPropsWithoutRef<typeof RouterNavLink>
>(({ children, className, ...props }, ref) => {
  return (
    <RouterNavLink
      {...props}
      ref={ref}
      className={({ isActive }) =>
        `text-sm font-medium uppercase transition-colors hover:text-yellow-400 data-[active=true]:text-yellow-400 ${
          isActive ? 'text-yellow-400' : 'text-white'
        } ${className}`
      }
    >
      {children}
    </RouterNavLink>
  )
})
NavLink.displayName = "NavLink"

const Header = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isDesktop = window.innerWidth >= 1024;
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const getHeaderBgClass = () => {
    if (isHomePage) {
      return isScrolled 
        ? "bg-black/85" 
        : "bg-gradient-to-b from-black/70 to-black/10";
    }
    return "bg-black/80";
  };
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getHeaderBgClass()}`}>
      <div className="container mx-auto px-4 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 md:pl-0 -ml-1 md:ml-0 min-w-[200px] md:min-w-[250px]">
          <Logo />
        </Link>
        
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={toggleMenu}>
            <Menu className="h-[1.875rem] w-[1.875rem] text-white" />
          </Button>
        )}
        
        <nav className={`${isMobile ? (isMenuOpen ? 'flex' : 'hidden') : 'flex'} ${
          isMobile 
            ? 'fixed top-20 left-0 right-0 bg-black/95 flex-col items-start p-5 gap-y-4 h-[calc(100vh-5rem+30px)] pl-[20px] md:pl-[20%] divide-y divide-white/25'
            : 'items-center gap-x-6 px-[75px]'
        }`}>
          <NavLink to="/">HOME</NavLink>
          <NavLink to="/attend">ATTEND</NavLink>
          <NavLink to="/speakers">SPEAKERS</NavLink>
          <NavLink to="/schedule">SCHEDULE</NavLink>
          <NavLink to="/venue">VENUE</NavLink>
          {isDesktop && <NavLink to="/tickets">TICKETS</NavLink>}
          <NavLink to="/exhibition">EXPO</NavLink>
          <NavLink to="/contact">CONTACT</NavLink>
          
          {isAuthenticated && (
            <NavLink to="/admin-dashboard">ADMIN DASHBOARD</NavLink>
          )}
          
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="text-sm">
                  {user?.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/tickets">
              <Button 
                className="bg-[#ea384c] hover:bg-[#c52c3f] text-white text-sm uppercase tracking-wider font-bold"
              >
                BOOK NOW
              </Button>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
